import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '@wlb/shared/api/services/authService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { validateEmail } from '@wlb/shared/utils/validation';
import type { ApiError } from '@wlb/shared/types';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import type { AuthFlowState } from '../features/auth/authFlowState';
import '../features/auth/auth.css';

export function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const result = validateEmail(email);
    if (!result.valid) {
      setError(t(result.message));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await authService.forgotPassword({ email: email.trim() });
      const next: AuthFlowState = {
        email: email.trim(),
        passwordResetToken: response.data?.passwordResetToken ?? '',
      };
      navigate('/verification-code', { state: next });
    } catch (err) {
      ToastService.show({ type: 'error', message: getApiErrorMessage(err as ApiError) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <button className="auth-flow__back" type="button" onClick={() => navigate('/login')}>
          ‹ {t('common.buttons.back')}
        </button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.forgotPassword.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.forgotPassword.subheading')}</p>

        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <label className="auth-flow__field">
            <span>{t('auth.forgotPassword.emailLabel')}</span>
            <input
              className={`auth-flow__input${error ? ' auth-flow__input--error' : ''}`}
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder={t('auth.forgotPassword.emailPlaceholder')}
              autoComplete="email"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'forgot-password-error' : undefined}
              autoFocus
            />
            <FormError message={error} id="forgot-password-error" />
          </label>
          <button className="auth-flow__primary" type="submit" disabled={loading}>
            {loading ? t('common.loading') : t('auth.forgotPassword.sendResetLink')}
          </button>
        </form>

        <Link to="/login" className="auth-flow__footer">{t('auth.changePassword.cancelBackToSignIn')}</Link>
      </div>
    </AuthFlowLayout>
  );
}
